import { Modal } from "components";
import { FieldGroup } from "components";
import { EmployeeModel } from "models";
import { useState } from "react";
import { Button, Form, Select } from "semantic-ui-react";
import { useAuth, useUI } from "services";
import { USER_MSG, getTranslatedUserMsg } from "strings";
import { useT } from "i18n";
import { useEmployeeMutations } from "hooks/mutations";

interface AddModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyMappings: { value: string; text: string }[];
  hasEditAccess: boolean;
  hasAccessToCompanyDropdown: boolean;
  isFromCompanyDetailRoute?: boolean;
  dealerId?: string;
}

export default function AddModal({
  isOpen,
  onClose,
  companyMappings,
  hasEditAccess,
  hasAccessToCompanyDropdown,
  isFromCompanyDetailRoute,
  dealerId,
}: AddModalProps) {
  const t = useT();
  const { user } = useAuth();
  const { showError } = useUI();
  const { addEmployee, addPending } = useEmployeeMutations();

  const dealerIdToUse = isFromCompanyDetailRoute ? dealerId : user?.dealerId;
  const [employeeModel, setEmployeeModel] = useState(
    EmployeeModel({
      isActive: false,
      dealerId: dealerIdToUse,
      role: isFromCompanyDetailRoute ? "admin" : "",
    }),
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("section.employee.newEmployee", "New Employee")}
      actions={
        <div className={"controls"}>
          <Button
            basic
            content={t("component.common.cancel", "Cancel")}
            onClick={onClose}
            disabled={addPending}
          />
          {hasEditAccess && (
            <Button
              primary
              content={t("section.employee.submit", "Submit")}
              className={"control-right"}
              disabled={addPending}
              loading={addPending}
              onClick={() => {
                if (!employeeModel.isValid()) {
                  showError(getTranslatedUserMsg(t, "ERROR_INVALID"));
                  return;
                }
                addEmployee(employeeModel.data, {
                  onSuccess: () => {
                    onClose();
                  },
                });
              }}
            />
          )}
        </div>
      }
    >
      <Form className={"row"} style={{ gap: "22px", justifyContent: "space-evenly" }}>
        <FieldGroup
          fields={"name phone isActive"}
          disabled={!hasEditAccess}
          model={employeeModel}
        />
        <div>
          {hasAccessToCompanyDropdown && (
            <div className={"field"}>
              <label>{t("section.employee.company", "Company")}</label>
              <Select
                search
                placeholder={
                  companyMappings.find(
                    (company: { value: string; text: string }) =>
                      company.value === employeeModel.data.dealerId,
                  )?.text || t("section.employee.selectCompany", "Select Company")
                }
                options={companyMappings}
                onChange={(_, { value }: any) => {
                  setEmployeeModel(
                    EmployeeModel({
                      ...employeeModel.data,
                      dealerId: value,
                    }),
                  );
                }}
                disabled={isFromCompanyDetailRoute}
                value={dealerIdToUse}
                forceSelection={false}
                selectOnBlur={false}
                data-cy={"employee-status-select"}
              />
            </div>
          )}
          <FieldGroup
            fields={"role email"}
            disabled={!hasEditAccess}
            model={employeeModel}
          />
        </div>
      </Form>
    </Modal>
  );
}
